import {
  RESERVED_PAGE_SLUGS,
  createPageSchema,
  customPageSchema,
  okSchema,
  pageSlugSchema,
  type CustomPageSummary,
} from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createFileRoute,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router';
import {
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Globe,
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  Rocket,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Confirm } from '@/components/confirm';
import { pageStatus, suggestSlug } from '@/components/customisation/builder/page-helpers';
import { FormField } from '@/components/form-field';
import { EmptyState, SectionHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { apiRequest } from '@/lib/api';
import { pagesQuery, studioBase } from '@/lib/customisation/queries';
import { setPreviewMode } from '@/lib/customisation/preview';
import { errorMessage } from '@/lib/forms';
import { timeAgo } from '@/lib/format';
import { useStudioTenant } from '../customisation';

export const Route = createFileRoute('/_app/admin/customisation/pages')({ component: Pages });

/** The pages tab: the list, or the editor when a page route is open underneath. */
function Pages() {
  const child = useChildMatches();
  if (child.length > 0) return <Outlet />;
  return <PageList />;
}

function PageList() {
  const tenant = useStudioTenant();
  const search = Route.useSearch();
  const pages = useQuery(pagesQuery(tenant));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['studio', tenant, 'pages'] });

  const act = useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: 'publish' | 'unpublish' | 'duplicate' | 'delete';
    }) => {
      if (action === 'delete') {
        await apiRequest(`${studioBase(tenant)}/pages/${id}`, {
          method: 'DELETE',
          schema: okSchema,
        });
        return { action };
      }
      const page = await apiRequest(`${studioBase(tenant)}/pages/${id}/${action}`, {
        method: 'POST',
        body: {},
        schema: customPageSchema,
      });
      return { action, page };
    },
    onSuccess: async (r) => {
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['customisation'] });
      if (r.action === 'publish') toast.success('Published. Customers can open it now.');
      if (r.action === 'unpublish') toast.success('Unpublished. The page is a draft again.');
      if (r.action === 'delete') toast.success('Page deleted.');
      if (r.action === 'duplicate' && 'page' in r && r.page) {
        toast.success('Page duplicated.');
        await navigate({
          to: '/admin/customisation/pages/$pageId',
          params: { pageId: r.page.id },
          search,
        });
      }
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <div className="mx-auto max-w-[1100px] px-4 py-6 sm:px-6 lg:px-8">
      <SectionHeader
        title="Pages"
        description="Custom pages for customers (in the sidebar) or for everyone (linked from the login page)."
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus /> New page
          </Button>
        }
      />
      <QueryState query={pages}>
        {({ pages: list }) =>
          list.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No pages yet"
              action={
                <Button size="sm" onClick={() => setCreating(true)}>
                  <Plus /> Create the first page
                </Button>
              }
            >
              A getting-started guide, rules, a status page: anything built from the same components
              as the dashboard.
            </EmptyState>
          ) : (
            <ul className="bg-card shadow-card divide-y rounded-xl border">
              {list.map((p) => {
                const status = pageStatus(p);
                const busy = act.isPending && act.variables?.id === p.id;
                return (
                  <li key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <span className="bg-elevated text-muted-foreground grid size-9 shrink-0 place-items-center rounded-lg border">
                      <FileText className="size-4" aria-hidden />
                    </span>
                    <div className="grid min-w-0 flex-1 basis-48 leading-tight">
                      <Link
                        to="/admin/customisation/pages/$pageId"
                        params={{ pageId: p.id }}
                        search={search}
                        className="truncate text-sm font-medium hover:underline"
                      >
                        {p.title}
                      </Link>
                      <span className="text-muted-foreground truncate font-mono text-xs">
                        /custom/{p.slug}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant={status.variant}>{status.label}</Badge>
                      <Badge
                        variant="outline"
                        title={
                          p.visibility === 'public'
                            ? 'Anyone can open it'
                            : 'Signed-in customers only'
                        }
                      >
                        {p.visibility === 'public' ? <Globe /> : <Lock />}
                        {p.visibility === 'public' ? 'Public' : 'Customers'}
                      </Badge>
                      <Badge
                        variant="outline"
                        title={p.showInNav ? 'Shown in the sidebar' : 'Not in the sidebar'}
                      >
                        {p.showInNav ? <Eye /> : <EyeOff />}
                        {p.showInNav ? 'In nav' : 'Hidden'}
                      </Badge>
                    </div>
                    <span
                      className="text-muted-foreground w-24 shrink-0 text-right text-xs"
                      title="Draft last saved"
                    >
                      {timeAgo(p.draftUpdatedAt)}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button asChild size="sm" variant="outline">
                        <Link
                          to="/admin/customisation/pages/$pageId"
                          params={{ pageId: p.id }}
                          search={search}
                        >
                          <Pencil /> Edit
                        </Link>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon-sm"
                            variant="ghost"
                            aria-label={`More actions for ${p.title}`}
                            disabled={busy}
                          >
                            {busy ? <Loader2 className="animate-spin" /> : <MoreHorizontal />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setPreviewMode(true);
                              window.open(
                                `/custom/${encodeURIComponent(p.slug)}?preview=draft`,
                                '_blank',
                                'noopener',
                              );
                            }}
                          >
                            <ExternalLink /> Preview draft
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => act.mutate({ id: p.id, action: 'duplicate' })}
                          >
                            <Copy /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <PublishItem
                            page={p}
                            onAction={(action) => act.mutate({ id: p.id, action })}
                          />
                          <DropdownMenuSeparator />
                          <DeleteItem
                            page={p}
                            onDelete={() => act.mutate({ id: p.id, action: 'delete' })}
                          />
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </li>
                );
              })}
            </ul>
          )
        }
      </QueryState>
      <NewPageDialog
        open={creating}
        onOpenChange={setCreating}
        tenant={tenant}
        existing={pages.data?.pages.map((p) => p.slug) ?? []}
        onCreated={async (id) => {
          await invalidate();
          await navigate({
            to: '/admin/customisation/pages/$pageId',
            params: { pageId: id },
            search,
          });
        }}
      />
    </div>
  );
}

/** Publish/unpublish rows ask for confirmation; the dialog opens from the menu item. */
function PublishItem({
  page,
  onAction,
}: {
  page: CustomPageSummary;
  onAction: (action: 'publish' | 'unpublish') => void;
}) {
  const published = Boolean(page.publishedAt);
  return (
    <Confirm
      title={published ? 'Unpublish this page?' : 'Publish this page?'}
      description={
        published
          ? 'Customers lose access to it until it is published again. The draft is kept.'
          : `Customers can open /custom/${page.slug}${page.showInNav ? ' and it appears in their sidebar' : ''}.`
      }
      action={published ? 'Unpublish' : 'Publish'}
      destructive={published}
      onConfirm={() => onAction(published ? 'unpublish' : 'publish')}
    >
      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
        {published ? <EyeOff /> : <Rocket />} {published ? 'Unpublish' : 'Publish'}
      </DropdownMenuItem>
    </Confirm>
  );
}

function DeleteItem({ page, onDelete }: { page: CustomPageSummary; onDelete: () => void }) {
  return (
    <Confirm
      title={`Delete “${page.title}”?`}
      description="The page, its draft and its history are removed. This can't be undone."
      action="Delete page"
      destructive
      onConfirm={onDelete}
    >
      <DropdownMenuItem variant="destructive" onSelect={(e) => e.preventDefault()}>
        <Trash2 /> Delete
      </DropdownMenuItem>
    </Confirm>
  );
}

function NewPageDialog({
  open,
  onOpenChange,
  tenant,
  existing,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: string;
  existing: string[];
  onCreated: (id: string) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [visibility, setVisibility] = useState<'customers' | 'public'>('customers');
  const [showInNav, setShowInNav] = useState(true);
  const effectiveSlug = slugTouched ? slug : suggestSlug(title);
  const slugCheck = pageSlugSchema.safeParse(effectiveSlug);
  const slugError = !effectiveSlug
    ? undefined
    : !slugCheck.success
      ? slugCheck.error.issues[0]?.message
      : RESERVED_PAGE_SLUGS.has(effectiveSlug)
        ? 'That slug is used by the panel itself.'
        : existing.includes(effectiveSlug)
          ? 'A page with that slug already exists.'
          : undefined;
  const create = useMutation({
    mutationFn: () =>
      apiRequest(`${studioBase(tenant)}/pages`, {
        method: 'POST',
        body: createPageSchema.parse({
          title: title.trim(),
          slug: effectiveSlug,
          visibility,
          showInNav,
        }),
        schema: customPageSchema,
      }),
    onSuccess: async (page) => {
      onOpenChange(false);
      setTitle('');
      setSlug('');
      setSlugTouched(false);
      toast.success('Page created. It stays a draft until you publish it.');
      await onCreated(page.id);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const valid = title.trim().length > 0 && effectiveSlug.length > 0 && !slugError;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) create.mutate();
          }}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>New page</DialogTitle>
            <DialogDescription>
              The page starts empty and as a draft. Build it, then publish.
            </DialogDescription>
          </DialogHeader>
          <FormField label="Title">
            <Input
              value={title}
              maxLength={120}
              autoFocus
              placeholder="Getting started"
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormField>
          <FormField
            label="Slug"
            error={slugError}
            description={
              effectiveSlug
                ? `Customers open it at /custom/${effectiveSlug}`
                : 'Lowercase letters, numbers and dashes.'
            }
          >
            <Input
              value={effectiveSlug}
              maxLength={64}
              placeholder="getting-started"
              className="font-mono"
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
            />
          </FormField>
          <FormField label="Who can open it">
            <Select
              value={visibility}
              onValueChange={(v) => setVisibility(v as 'customers' | 'public')}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="customers">Signed-in customers</SelectItem>
                <SelectItem value="public">
                  Anyone (also at /p/slug, linkable from the login page)
                </SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="new-page-nav">Show in the sidebar once published</Label>
            <Switch id="new-page-nav" checked={showInNav} onCheckedChange={setShowInNav} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />} Create page
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
